import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string, unknown } from "../../lazy-zod.js";
import { optionalBooleanQuerySchema } from "../protocol/shared.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { projectSchema, repositoryFingerprintSchema } from "./projects.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
//#region src/schemas/http/agent-activity.ts
const AGENT_ACTIVITY_SNAPSHOT_PROJECT_LIMIT = 1e3;
const AGENT_ACTIVITY_TITLE_MAX_CHARS = 240;
const AGENT_ACTIVITY_PRESENCE_TTL_MAX_MS = 5 * 6e4;
const AGENT_ACTIVITY_EVENT_MAX_BYTES = 16 * 1024;
const AGENT_ACTIVITY_EVENT_BATCH_LIMIT = 64;
const AGENT_ACTIVITY_EVENT_BATCH_MAX_BYTES = 1052672;
const AGENT_ACTIVITY_EVENT_RETENTION_MS = 336 * 60 * 6e4;
const AGENT_ACTIVITY_DIGEST_SUMMARY_MAX_CHARS = 1200;
const AGENT_ACTIVITY_DIGEST_EVIDENCE_MAX_ITEMS = 100;
const AGENT_ACTIVITY_STATS_DAILY_LIMIT = 400;
const AGENT_ACTIVITY_PROVIDERS = [
	"codex",
	"claude-code",
	"cursor",
	"opencode"
];
const AGENT_ACTIVITY_CLIENT_KINDS = [
	"codex-app",
	"codex-cli",
	"claude-code-cli",
	"cursor-cli",
	"cursor-ide",
	"unknown"
];
const boundedIdentifierSchema = string().trim().min(1).max(256);
const timestampMsSchema = number().int().nonnegative();
const nullableBoundedTextSchema = (maximum) => string().trim().min(1).max(maximum).nullable();
const agentActivityRpcContextShape = { context: openMeldRequestContextSchema.optional() };
const agentActivityEventTypeSchema = enumFactory([
	"session_started",
	"session_updated",
	"session_active",
	"session_idle",
	"session_ended",
	"session_error",
	"heartbeat"
]);
const agentActivitySessionStatusSchema = enumFactory([
	"active",
	"idle",
	"ended",
	"error",
	"unknown"
]);
const agentActivityProviderSchema = enumFactory(AGENT_ACTIVITY_PROVIDERS);
const agentActivityClientKindSchema = enumFactory(AGENT_ACTIVITY_CLIENT_KINDS);
const safeMetadataTextSchema = string().trim().min(1).max(256);
const codexAgentActivityProviderMetadataSchema = strictObject({
	approvalPolicy: enumFactory([
		"untrusted",
		"on-failure",
		"on-request",
		"never"
	]).optional(),
	cliVersion: safeMetadataTextSchema.optional(),
	contextWindow: number().int().positive().optional(),
	modelProvider: safeMetadataTextSchema.optional(),
	originator: enumFactory([
		"desktop",
		"cli",
		"subagent"
	]).optional(),
	reasoningEffort: enumFactory([
		"minimal",
		"low",
		"medium",
		"high",
		"xhigh",
		"ultra"
	]).optional(),
	sandboxMode: enumFactory([
		"read-only",
		"workspace-write",
		"danger-full-access"
	]).optional(),
	source: enumFactory([
		"rollout",
		"hook",
		"reconciliation"
	]).optional()
});
const claudeCodeAgentActivityProviderMetadataSchema = strictObject({
	entrypoint: enumFactory(["cli", "desktop"]).optional(),
	isSidechain: boolean().optional(),
	parentSessionId: boundedIdentifierSchema.optional(),
	permissionMode: enumFactory([
		"default",
		"acceptEdits",
		"plan",
		"dontAsk",
		"bypassPermissions",
		"ask",
		"allow"
	]).optional(),
	source: enumFactory([
		"jsonl",
		"hook",
		"reconciliation"
	]).optional(),
	version: safeMetadataTextSchema.optional()
});
const openCodeAgentActivityProviderMetadataSchema = strictObject({
	agent: safeMetadataTextSchema.optional(),
	cost: number().nonnegative().finite().optional(),
	modelProvider: safeMetadataTextSchema.optional(),
	modelVariant: safeMetadataTextSchema.optional(),
	parentSessionId: boundedIdentifierSchema.optional(),
	tokensCacheRead: number().int().nonnegative().optional(),
	tokensCacheWrite: number().int().nonnegative().optional(),
	tokensInput: number().int().nonnegative().optional(),
	tokensOutput: number().int().nonnegative().optional(),
	tokensReasoning: number().int().nonnegative().optional(),
	version: safeMetadataTextSchema.optional()
});
const cursorAgentActivityProviderMetadataSchema = strictObject({
	composerMode: enumFactory([
		"agent",
		"chat",
		"plan",
		"debug"
	]).optional(),
	isBackgroundAgent: boolean().optional(),
	source: enumFactory(["acp", "hook"]),
	version: safeMetadataTextSchema.optional()
});
const codexPublicAgentActivityProviderMetadataSchema = codexAgentActivityProviderMetadataSchema;
const claudeCodePublicAgentActivityProviderMetadataSchema = claudeCodeAgentActivityProviderMetadataSchema.omit({ parentSessionId: true });
const cursorPublicAgentActivityProviderMetadataSchema = cursorAgentActivityProviderMetadataSchema;
const openCodePublicAgentActivityProviderMetadataSchema = openCodeAgentActivityProviderMetadataSchema.omit({ parentSessionId: true });
const agentActivityEventCommonShape = {
	eventId: boundedIdentifierSchema,
	eventType: agentActivityEventTypeSchema,
	expiresAtMs: timestampMsSchema,
	gitBranch: nullableBoundedTextSchema(512),
	gitCommit: nullableBoundedTextSchema(128),
	model: nullableBoundedTextSchema(256),
	observedAtMs: timestampMsSchema,
	organizationId: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	providerSessionId: string().trim().min(1).max(512),
	repositoryFingerprint: repositoryFingerprintSchema.nullable(),
	source: literal("observed"),
	startedAtMs: timestampMsSchema.nullable(),
	status: agentActivitySessionStatusSchema,
	title: nullableBoundedTextSchema(240)
};
const codexAgentActivityEventSchema = strictObject({
	...agentActivityEventCommonShape,
	clientKind: enumFactory([
		"codex-app",
		"codex-cli",
		"unknown"
	]),
	provider: literal("codex"),
	providerMetadata: codexAgentActivityProviderMetadataSchema
});
const claudeCodeAgentActivityEventSchema = strictObject({
	...agentActivityEventCommonShape,
	clientKind: enumFactory(["claude-code-cli", "unknown"]),
	provider: literal("claude-code"),
	providerMetadata: claudeCodeAgentActivityProviderMetadataSchema
});
const openCodeAgentActivityEventSchema = strictObject({
	...agentActivityEventCommonShape,
	clientKind: literal("unknown"),
	provider: literal("opencode"),
	providerMetadata: openCodeAgentActivityProviderMetadataSchema
});
const agentActivityEventSchema = discriminatedUnion("provider", [
	codexAgentActivityEventSchema,
	claudeCodeAgentActivityEventSchema,
	strictObject({
		...agentActivityEventCommonShape,
		clientKind: enumFactory([
			"cursor-cli",
			"cursor-ide",
			"unknown"
		]),
		provider: literal("cursor"),
		providerMetadata: cursorAgentActivityProviderMetadataSchema
	}),
	openCodeAgentActivityEventSchema
]).superRefine((value, context) => {
	if (!{
		heartbeat: ["active"],
		session_active: ["active"],
		session_ended: ["ended"],
		session_error: ["error"],
		session_idle: ["idle"],
		session_started: ["active", "unknown"],
		session_updated: ["unknown"]
	}[value.eventType].includes(value.status)) context.addIssue({
		code: "custom",
		message: "Agent Activity event type and session status are inconsistent.",
		path: ["status"]
	});
	if (value.startedAtMs !== null && value.startedAtMs > value.observedAtMs) context.addIssue({
		code: "custom",
		message: "Agent Activity start time cannot follow its observation.",
		path: ["startedAtMs"]
	});
	if (value.status === "active" && value.expiresAtMs <= value.observedAtMs) context.addIssue({
		code: "custom",
		message: "Active Agent Activity requires a bounded future expiry.",
		path: ["expiresAtMs"]
	});
	if (value.expiresAtMs - value.observedAtMs > 3e5) context.addIssue({
		code: "custom",
		message: "Agent Activity expiry exceeds the presence TTL.",
		path: ["expiresAtMs"]
	});
	if (value.expiresAtMs < value.observedAtMs) context.addIssue({
		code: "custom",
		message: "Agent Activity expiry cannot precede its observation.",
		path: ["expiresAtMs"]
	});
});
const agentActivityEventOutcomeSchema = enumFactory([
	"accepted",
	"reused",
	"retryable_rejected",
	"permanent_rejected"
]);
const agentActivityEventRejectionReasonSchema = enumFactory([
	"event_id_conflict",
	"event_invalid",
	"event_time_invalid",
	"membership_removed",
	"membership_unavailable",
	"project_archived",
	"project_missing",
	"repository_fingerprint_mismatch",
	"runtime_lookup_conflict",
	"runtime_missing",
	"openmeld_service_runtime_id_invalid",
	"openmeld_service_runtime_id_mismatch",
	"openmeld_service_runtime_identity_invalid"
]);
const submitAgentActivityEventsBodyShape = {
	events: array(unknown()).min(1).max(64),
	lane: boundedIdentifierSchema,
	openMeldServiceRuntimeId: strictObject({
		source: literal("runtime_registry_authority"),
		value: string().trim().min(1).max(512)
	})
};
const submitAgentActivityEventsBodySchema = strictObject(submitAgentActivityEventsBodyShape);
const submitAgentActivityEventResultSchema = strictObject({
	eventId: boundedIdentifierSchema.optional(),
	index: number().int().nonnegative().max(63),
	outcome: agentActivityEventOutcomeSchema,
	reason: agentActivityEventRejectionReasonSchema.optional()
}).superRefine((value, context) => {
	if ((value.outcome === "retryable_rejected" || value.outcome === "permanent_rejected") !== Boolean(value.reason)) context.addIssue({
		code: "custom",
		message: "Rejected Agent Activity events require exactly one reason.",
		path: ["reason"]
	});
});
const submitAgentActivityEventsResponseSchema = strictObject({
	events: array(submitAgentActivityEventResultSchema).min(1).max(64),
	status: enumFactory([
		"accepted",
		"partial",
		"rejected"
	]),
	openMeldServiceRuntimeId: strictObject({
		source: literal("runtime_registry_authority"),
		value: string().trim().min(1).max(512)
	}).optional()
}).superRefine((value, context) => {
	const indices = value.events.map((event) => event.index);
	if (new Set(indices).size !== indices.length) context.addIssue({
		code: "custom",
		message: "Agent Activity event result indices must be unique.",
		path: ["events"]
	});
	if (value.status !== "rejected" && !value.openMeldServiceRuntimeId) context.addIssue({
		code: "custom",
		message: "Accepted Agent Activity responses require Runtime authority.",
		path: ["openMeldServiceRuntimeId"]
	});
});
const submitAgentActivityEventsRpcInputSchema = strictObject({
	...agentActivityRpcContextShape,
	...submitAgentActivityEventsBodyShape,
	deviceId: boundedIdentifierSchema
});
const agentActivitySnapshotQueryShape = {
	includeProviderMetadata: optionalBooleanQuerySchema,
	memberUserId: boundedIdentifierSchema.optional(),
	projectId: boundedIdentifierSchema.optional(),
	provider: agentActivityProviderSchema.optional(),
	status: agentActivitySessionStatusSchema.optional()
};
const agentActivitySnapshotQuerySchema = strictObject(agentActivitySnapshotQueryShape);
const agentActivityPublicSessionCommonShape = {
	expiresAtMs: timestampMsSchema,
	gitBranch: nullableBoundedTextSchema(512),
	gitCommit: nullableBoundedTextSchema(128),
	id: boundedIdentifierSchema,
	isActive: boolean(),
	lastObservedAtMs: timestampMsSchema,
	memberName: string().trim().min(1).max(256),
	memberUserId: boundedIdentifierSchema,
	model: nullableBoundedTextSchema(256),
	projectId: boundedIdentifierSchema,
	startedAtMs: timestampMsSchema.nullable(),
	status: agentActivitySessionStatusSchema,
	title: nullableBoundedTextSchema(240)
};
const agentActivityPublicSessionSchema = discriminatedUnion("provider", [
	strictObject({
		...agentActivityPublicSessionCommonShape,
		clientKind: enumFactory([
			"codex-app",
			"codex-cli",
			"unknown"
		]),
		provider: literal("codex"),
		providerMetadata: codexPublicAgentActivityProviderMetadataSchema.optional()
	}),
	strictObject({
		...agentActivityPublicSessionCommonShape,
		clientKind: enumFactory(["claude-code-cli", "unknown"]),
		provider: literal("claude-code"),
		providerMetadata: claudeCodePublicAgentActivityProviderMetadataSchema.optional()
	}),
	strictObject({
		...agentActivityPublicSessionCommonShape,
		clientKind: enumFactory([
			"cursor-cli",
			"cursor-ide",
			"unknown"
		]),
		provider: literal("cursor"),
		providerMetadata: cursorPublicAgentActivityProviderMetadataSchema.optional()
	}),
	strictObject({
		...agentActivityPublicSessionCommonShape,
		clientKind: literal("unknown"),
		provider: literal("opencode"),
		providerMetadata: openCodePublicAgentActivityProviderMetadataSchema.optional()
	})
]);
const agentActivityPublicEventSchema = strictObject({
	clientKind: agentActivityClientKindSchema,
	eventId: boundedIdentifierSchema,
	eventType: agentActivityEventTypeSchema,
	memberName: string().trim().min(1).max(256),
	memberUserId: boundedIdentifierSchema,
	observedAtMs: timestampMsSchema,
	projectId: boundedIdentifierSchema,
	provider: agentActivityProviderSchema,
	status: agentActivitySessionStatusSchema,
	title: nullableBoundedTextSchema(240)
});
const agentActivityDigestCitationSchema = strictObject({
	eventId: boundedIdentifierSchema,
	observedAtMs: timestampMsSchema
});
const agentActivityPublicDigestSchema = strictObject({
	citations: array(agentActivityDigestCitationSchema).max(20),
	createdAtMs: timestampMsSchema,
	eventCount: number().int().positive(),
	failureReason: string().trim().min(1).max(256).nullable(),
	id: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	status: enumFactory(["completed", "failed"]),
	summary: string().trim().min(1).max(AGENT_ACTIVITY_DIGEST_SUMMARY_MAX_CHARS).nullable(),
	windowEndedAtMs: timestampMsSchema,
	windowStartedAtMs: timestampMsSchema
}).superRefine((value, context) => {
	if (value.windowStartedAtMs > value.windowEndedAtMs) context.addIssue({
		code: "custom",
		message: "Agent Activity digest start cannot follow its end.",
		path: ["windowStartedAtMs"]
	});
	const completed = value.status === "completed";
	if (completed !== Boolean(value.summary)) context.addIssue({
		code: "custom",
		message: "Completed Agent Activity digests require exactly one summary.",
		path: ["summary"]
	});
	if (completed === Boolean(value.failureReason)) context.addIssue({
		code: "custom",
		message: "Failed Agent Activity digests require exactly one failure reason.",
		path: ["failureReason"]
	});
	if (completed !== value.citations.length > 0) context.addIssue({
		code: "custom",
		message: "Completed Agent Activity digests require evidence citations.",
		path: ["citations"]
	});
});
const agentActivityDigestEvidenceSchema = strictObject({
	eventId: boundedIdentifierSchema,
	eventType: agentActivityEventTypeSchema,
	memberName: string().trim().min(1).max(256),
	observedAtMs: timestampMsSchema,
	provider: agentActivityProviderSchema,
	status: agentActivitySessionStatusSchema,
	title: nullableBoundedTextSchema(240)
});
const summarizeAgentActivityDigestRpcInputSchema = strictObject({
	eventCount: number().int().positive(),
	evidence: array(agentActivityDigestEvidenceSchema).min(1).max(100),
	organizationId: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	windowEndedAtMs: timestampMsSchema,
	windowStartedAtMs: timestampMsSchema
}).superRefine((value, context) => {
	if (value.eventCount < value.evidence.length) context.addIssue({
		code: "custom",
		message: "Digest event count cannot be smaller than its evidence sample.",
		path: ["eventCount"]
	});
	if (value.windowStartedAtMs > value.windowEndedAtMs) context.addIssue({
		code: "custom",
		message: "Agent Activity digest start cannot follow its end.",
		path: ["windowStartedAtMs"]
	});
});
const summarizeAgentActivityDigestRpcResponseSchema = strictObject({
	citations: array(agentActivityDigestCitationSchema).min(1).max(20),
	summary: string().trim().min(1).max(AGENT_ACTIVITY_DIGEST_SUMMARY_MAX_CHARS)
}).superRefine((value, context) => {
	const keys = value.citations.map((citation) => `${citation.eventId}\0${citation.observedAtMs}`);
	if (new Set(keys).size !== keys.length) context.addIssue({
		code: "custom",
		message: "Agent Activity digest citations must be unique.",
		path: ["citations"]
	});
});
const agentActivitySnapshotResponseSchema = strictObject({
	asOfMs: timestampMsSchema,
	digestsTruncated: boolean(),
	organizationId: boundedIdentifierSchema,
	digests: array(agentActivityPublicDigestSchema).max(500),
	projects: array(projectSchema).max(AGENT_ACTIVITY_SNAPSHOT_PROJECT_LIMIT),
	recentEvents: array(agentActivityPublicEventSchema).max(100),
	sessions: array(agentActivityPublicSessionSchema).max(500),
	sessionsTruncated: boolean(),
	stalenessThresholdMs: number().int().positive()
});
const agentActivitySnapshotRpcInputSchema = strictObject({
	...agentActivityRpcContextShape,
	...agentActivitySnapshotQueryShape
});
const agentActivityStatsDailySchema = strictObject({
	activeDurationMs: number().int().nonnegative(),
	sessionCount: number().int().nonnegative(),
	utcDate: iso.date()
});
const agentActivityStatsQueryShape = { projectId: boundedIdentifierSchema.optional() };
const agentActivityStatsQuerySchema = strictObject(agentActivityStatsQueryShape);
const agentActivityStatsResponseSchema = strictObject({
	daily: array(agentActivityStatsDailySchema).max(400),
	dailyTruncated: boolean(),
	organizationId: boundedIdentifierSchema,
	sinceMs: timestampMsSchema.nullable(),
	totalActiveDurationMs: number().int().nonnegative(),
	totalSessions: number().int().nonnegative()
});
const agentActivityStatsRpcInputSchema = strictObject({
	...agentActivityRpcContextShape,
	...agentActivityStatsQueryShape
});
const centerAgentReadAgentActivityToolRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	...agentActivitySnapshotQueryShape,
	speakerMember: centerAgentToolSpeakerSchema
});
//#endregion
export { AGENT_ACTIVITY_CLIENT_KINDS, AGENT_ACTIVITY_DIGEST_EVIDENCE_MAX_ITEMS, AGENT_ACTIVITY_DIGEST_SUMMARY_MAX_CHARS, AGENT_ACTIVITY_EVENT_BATCH_LIMIT, AGENT_ACTIVITY_EVENT_BATCH_MAX_BYTES, AGENT_ACTIVITY_EVENT_MAX_BYTES, AGENT_ACTIVITY_EVENT_RETENTION_MS, AGENT_ACTIVITY_PRESENCE_TTL_MAX_MS, AGENT_ACTIVITY_PROVIDERS, AGENT_ACTIVITY_SNAPSHOT_PROJECT_LIMIT, AGENT_ACTIVITY_STATS_DAILY_LIMIT, AGENT_ACTIVITY_TITLE_MAX_CHARS, agentActivityClientKindSchema, agentActivityDigestCitationSchema, agentActivityDigestEvidenceSchema, agentActivityEventOutcomeSchema, agentActivityEventRejectionReasonSchema, agentActivityEventSchema, agentActivityEventTypeSchema, agentActivityProviderSchema, agentActivityPublicDigestSchema, agentActivityPublicEventSchema, agentActivityPublicSessionSchema, agentActivitySessionStatusSchema, agentActivitySnapshotQuerySchema, agentActivitySnapshotResponseSchema, agentActivitySnapshotRpcInputSchema, agentActivityStatsDailySchema, agentActivityStatsQuerySchema, agentActivityStatsResponseSchema, agentActivityStatsRpcInputSchema, centerAgentReadAgentActivityToolRpcInputSchema, claudeCodeAgentActivityProviderMetadataSchema, claudeCodePublicAgentActivityProviderMetadataSchema, codexAgentActivityProviderMetadataSchema, codexPublicAgentActivityProviderMetadataSchema, cursorAgentActivityProviderMetadataSchema, cursorPublicAgentActivityProviderMetadataSchema, openCodeAgentActivityProviderMetadataSchema, openCodePublicAgentActivityProviderMetadataSchema, submitAgentActivityEventResultSchema, submitAgentActivityEventsBodySchema, submitAgentActivityEventsResponseSchema, submitAgentActivityEventsRpcInputSchema, summarizeAgentActivityDigestRpcInputSchema, summarizeAgentActivityDigestRpcResponseSchema };

//# sourceMappingURL=agent-activity.js.map